import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces } from 'react-i18next';
import { isTrialExpired } from 'utils/freeTrials';
import { isNaN, isNil } from 'lodash';
import Paragraph from 'components/Paragraph';
import Tooltip from 'components/Tooltip';
import { Date } from 'apps/components/date';

import { formatAmount } from 'utils/formatStrings';

export const PlanDetails = withNamespaces()(
  observer(({ paymentSpace, t }) => {
    if (!paymentSpace) return null;
    const {
      subscription,
      seatsUsage,
      inSpace: { trialPeriodEnd },
      creditCards,
      invoices,
    } = paymentSpace;
    const { freeSeats = 0 } = seatsUsage || {};

    const {
      currentPeriodEnd,
      cancelAtPeriodEnd,
      isCancelled,
      nextAmount,
      discount,
      credits,
    } = subscription || {};

    const isTrialActive = trialPeriodEnd && !isTrialExpired(trialPeriodEnd);

    // If the subscription is cancelled during free trial, but it's plan will not be end
    // so the next amount is 0
    const nextPaymentAmount =
      isTrialActive && cancelAtPeriodEnd
        ? formatAmount(0)
        : formatAmount(nextAmount / 100);

    const getPaymentPrefix = () => {
      if (isCancelled) {
        return 'Last';
      }

      if (isTrialActive) {
        return 'First';
      }

      return 'Next';
    };

    const getPaymentAmount = () => {
      if (isCancelled) {
        const paidAmount = invoices.apps.find(
          app => app.status === 'PAID',
        ).amount;

        if (!isNaN(paidAmount)) {
          return formatAmount(paidAmount / 100);
        }

        return formatAmount(0);
      }

      return nextPaymentAmount;
    };

    const getPaymentPeriodEnd = () => {
      if (isCancelled) {
        const { periodEnd } = invoices.apps.find(app => app.status === 'PAID');

        return periodEnd;
      }

      return currentPeriodEnd;
    };

    const renderSubscriptionDetails = () => {
      if (creditCards.length === 0 && currentPeriodEnd && !isCancelled) {
        return (
          <Paragraph>
            <span className='pr-2'>
              Your plan will be canceled at{' '}
              <Date
                dataTestId='planDetails-paymentDue-text'
                value={currentPeriodEnd}
                noRelative
              />
              .
            </span>
          </Paragraph>
        );
      }

      const periodEnd = getPaymentPeriodEnd();
      return (
        (periodEnd || !isNil(nextAmount)) && (
          <div className='w-2/4'>
            {periodEnd && (
              <Paragraph>
                <strong className='pr-2'>
                  {t(`${getPaymentPrefix()} payment due:`)}
                </strong>
                <Date
                  dataTestId='planDetails-paymentDue-text'
                  value={periodEnd}
                  noRelative
                />
              </Paragraph>
            )}
            {!isNil(nextAmount) && (
              <Paragraph>
                <strong className='pr-2'>
                  {t(`${getPaymentPrefix()} payment amount:`)}
                </strong>
                <span
                  className='text-black'
                  data-testid='planDetails-paymentAmount-text'
                >
                  <span data-testid='planDetails-nextPaymentAmount-text'>
                    {getPaymentAmount()}
                  </span>
                  <Tooltip id='creditBalance' delayShow={500} />
                  {!!credits && (
                    <span
                      data-for='creditBalance'
                      data-tip={t(
                        'Balance which will be deducted for the next invoice',
                      )}
                    >
                      <>
                        {` (${t('credit balance')}: `}
                        <span data-testid='planDetails-creditBalance-text'>
                          {formatAmount(credits / 100)}
                        </span>
                        {`)`}
                      </>
                    </span>
                  )}
                </span>
              </Paragraph>
            )}
          </div>
        )
      );
    };

    return (
      <div className='lg:flex md:flex align-top'>
        {renderSubscriptionDetails()}
        <div className='w-2/4'>
          {!!freeSeats && (
            <Paragraph>
              <strong className='pr-2'>{t('Free Seats:')}</strong>
              <span>{freeSeats}</span>
            </Paragraph>
          )}

          {!!discount && (
            <Paragraph>
              <strong className='pr-2'>{t('Discount:')}</strong>
              <span>{`${discount} %`}</span>
            </Paragraph>
          )}
        </div>
      </div>
    );
  }),
);

PlanDetails.propTypes = {
  subscription: PropTypes.object,
};
